/**
 * ConfirmDialog - Custom styled confirm dialog
 *
 * Mount once in root.tsx. Controlled by useConfirmStore / confirmDialog().
 *
 * @module ConfirmDialog
 */

import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "~/core/utils/cn";
import { useConfirmStore } from "~/core/store/useConfirmStore";
import { useScrollLock } from "~/core/hooks/useScrollLock";

export function ConfirmDialog() {
  const { isOpen, options, close } = useConfirmStore();
  useScrollLock(isOpen);

  // ESC to cancel
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") close(false);
    },
    [close]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!options) return null;

  const {
    title,
    message,
    confirmText = "확인",
    cancelText = "취소",
    danger = false,
  } = options;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="confirm-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[9999] scrim"
            onClick={() => close(false)}
          />

          {/* Dialog */}
          <motion.div
            key="confirm-dialog"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
          >
            <div
              className={cn(
                "w-full max-w-[340px]",
                "bg-surface border border-line",
                "rounded-2xl shadow-xl shadow-black/20",
                "overflow-hidden",
                "form-island"
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Content */}
              <div className="px-6 pt-6 pb-4">
                <p className="text-[15px] font-semibold text-main leading-snug">
                  {title}
                </p>
                {message && (
                  <p className="text-sm text-muted mt-2 leading-relaxed whitespace-pre-line">
                    {message}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2.5 px-6 pb-5">
                {cancelText && (
                  <button
                    onClick={() => close(false)}
                    className={cn(
                      "flex-1 py-2.5 rounded-xl",
                      "text-sm font-medium",
                      "bg-elevated text-muted",
                      "hover:bg-emphasis",
                      "active:scale-[0.97]",
                      "transition-all duration-100"
                    )}
                  >
                    {cancelText}
                  </button>
                )}
                <button
                  autoFocus
                  onClick={() => close(true)}
                  className={cn(
                    "flex-1 py-2.5 rounded-xl",
                    "text-sm font-semibold",
                    "active:scale-[0.97]",
                    "transition-all duration-100",
                    danger
                      ? "bg-error-vivid text-white hover:brightness-110"
                      : "bg-accent text-on-accent bg-accent-hover"
                  )}
                >
                  {confirmText}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
